import * as React from 'react';
import { navigate, PageProps } from 'gatsby';
import { httpsCallable } from 'firebase/functions';
import { FirebaseError } from 'firebase/app';
import { functions } from '../../firebase';
import SignInPageLayout from '../../components/SignInPageLayout';
import Button from '../../components/Button';
import FirebaseContext from '../../contexts/FirebaseContext';
import { InviteModal, Membership } from '../../types';
import { frontendHost, pages, storageKeys } from '../../../config';
import Modal from '../../components/Modal';
import LoadingScreen from '../../components/LoadingScreen';
import ErrorScreen from '../../components/ErrorScreen';

const isBrowser = typeof window !== 'undefined';

type FamilyOrganizer = {
  organizer: string | undefined;
  subscriptionType?: 'business';
};

type Response = {
  success: boolean;
  message: string;
};

const getModals = (planType: 'family' | 'business'): InviteModal[] => {
  const planLabel = planType === 'business' ? 'team' : 'family';
  const groupType = planType === 'business' ? 'team' : 'family group';

  return [
    {
      id: 'family-exists',
      title: `You are already a ${planLabel} member`,
      primaryButton: {
        content: 'Membership settings',
        url: pages.memberMembershipSettings,
      },
      secondaryButtons: [
        {
          content: 'Go home',
          url: pages.home,
        },
      ],
      content: `It looks like you are already a member of a ${groupType}. Please leave your current ${groupType} first to join this ${planLabel}. Thank you!`,
    },
    {
      id: 'already-member',
      title: 'You are already a member',
      primaryButton: {
        content: 'Membership settings',
        url: pages.memberMembershipSettings,
      },
      secondaryButtons: [
        {
          content: 'Go home',
          url: pages.home,
        },
      ],
      content: `It looks like you already have an active membership. Please cancel your current membership first, then try to join the ${planLabel}. Thank you!`,
    },
    {
      id: 'already-family',
      title: `You are already a ${planLabel} member`,
      primaryButton: {
        content: 'Membership settings',
        url: pages.memberMembershipSettings,
      },
      secondaryButtons: [
        {
          content: 'Go home',
          url: pages.home,
        },
      ],
      content: `You are already a member of this ${planLabel}. Thank you!`,
    },
    {
      id: 'family-full',
      title: 'No more slots available',
      primaryButton: {
        content: 'Go home',
        url: pages.home,
      },
      secondaryButtons: [],
      content: `The ${planLabel} has reached its maximum limit, and no more slots are available. Please contact the plan manager. Thank you!`,
    },
  ];
};

const MODALS = {
  hasMembership: 'already-member',
  hasFamily: 'family-exists',
  alreadyMember: 'already-family',
  familyFull: 'family-full',
};

export default function InvitePage({ params }: PageProps) {
  const { inviteCode } = params;
  const [isLoading, setIsLoading] = React.useState(true);
  const [isUserSignedIn, setIsUserSignedIn] = React.useState(false);
  const [showModal, setShowModal] = React.useState({ id: '', open: false });
  const [error, setError] = React.useState<string | null>(null);
  const [familyOrganizer, setFamilyOrganizer] = React.useState<string | null>(null);
  const [planType, setPlanType] = React.useState<'family' | 'business'>('family');
  const [modalPlanType, setModalPlanType] = React.useState<'family' | 'business'>('family');
  const planLabel = planType === 'business' ? 'business plan' : 'family';
  const modals = getModals(modalPlanType);

  const { initializing, user, member, loadingMembership, membership } =
    React.useContext(FirebaseContext);

  const handleError = React.useCallback(
    (errorCode: FirebaseError['code'], currentPlanType?: 'family' | 'business') => {
      // Set the modal plan type to ensure correct terminology
      if (currentPlanType) {
        setModalPlanType(currentPlanType);
      } else {
        setModalPlanType(planType);
      }

      if (errorCode.includes('resource-exhausted')) {
        setShowModal({ id: MODALS.familyFull, open: true });
        return;
      }
      if (errorCode.includes('already-exists')) {
        setShowModal({ id: MODALS.alreadyMember, open: true });
        return;
      }
      setError('Oops, something went wrong. Please try again later.');
    },
    [planType],
  );

  const getFamilyOrganizer = React.useCallback(async () => {
    try {
      // Check the invite code before allowing the user to join the family
      const checkInviteCode = httpsCallable<{ inviteCode: string }, FamilyOrganizer>(
        functions,
        'checkInviteCode',
      );

      const result = await checkInviteCode({ inviteCode });

      if (!result || !result.data) {
        // If the family organizer doesn't have a public profile, we use a generic message
        setFamilyOrganizer(null);
        setIsLoading(false);
        return;
      }

      const { subscriptionType, organizer: familyOrganizerDisplayName } = result.data;
      const detectedPlanType = subscriptionType || 'family';

      setFamilyOrganizer(familyOrganizerDisplayName || null);
      setIsLoading(false);
      setPlanType(detectedPlanType);
      setModalPlanType(detectedPlanType);
    } catch (e) {
      if (e instanceof FirebaseError && e.code) {
        let errorPlanType: 'family' | 'business' | undefined;

        if ((e as any).details && (e as any).details.subscriptionType) {
          errorPlanType = (e as any).details.subscriptionType;
        }

        handleError(e.code, errorPlanType);
        return;
      }
      setError('Oops, something went wrong. Please try again later.');
    }
  }, [inviteCode, handleError]);

  const joinTheFamily = React.useCallback(async () => {
    try {
      setIsLoading(true);
      const acceptInviteCode = httpsCallable<{ inviteCode: string }, Response>(
        functions,
        'acceptInviteCode',
      );

      const result = await acceptInviteCode({ inviteCode });

      if (result && result.data.success) {
        await user?.getIdToken(true);
        navigate(pages.memberProfileSetup, { replace: true });
      }
    } catch (e) {
      if (e instanceof FirebaseError && e.code) {
        let errorPlanType: 'family' | 'business' = planType;

        if ((e as any).details && (e as any).details.subscriptionType) {
          errorPlanType = (e as any).details.subscriptionType;
        }

        handleError(e.code, errorPlanType);
      }
      setError('Oops, something went wrong. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [handleError, inviteCode, user, planType]);

  React.useEffect(() => {
    if (!isBrowser) {
      return;
    }

    if (initializing || loadingMembership) {
      return; // wait for current user to load
    }

    if (user !== null) {
      setIsUserSignedIn(true);
    }

    if (!loadingMembership && membership !== Membership.None) {
      setShowModal({ id: MODALS.hasMembership, open: true });
      return;
    }

    getFamilyOrganizer();
  }, [
    error,
    getFamilyOrganizer,
    initializing,
    inviteCode,
    loadingMembership,
    member,
    membership,
    user,
  ]);

  const handleJoinFamily = React.useCallback(() => {
    joinTheFamily();
  }, [joinTheFamily]);

  const redirectToSignIn = React.useCallback(() => {
    if (isBrowser) {
      window.localStorage.setItem(
        storageKeys.redirectAfterSignIn,
        pages.invite.replace(':inviteCode', inviteCode),
      );

      navigate(pages.signIn);
    }
  }, [inviteCode]);

  if (error) {
    return <ErrorScreen message={error} />;
  }

  return (
    <div>
      {isLoading ? (
        <LoadingScreen />
      ) : (
        <SignInPageLayout
          title={`You're invited ${familyOrganizer ? `by ${familyOrganizer}` : `to Earth.fm`}`}
          description={`Get unlimited access to our 1000+ high quality, immersive soundscapes from more than 150 field recordists around world. And other member only features by joining ${familyOrganizer ? `${familyOrganizer}'s ${planLabel}.` : `the ${planLabel}.`}`}
          hideHeader
          justifyContent="center"
        >
          <Button onClick={isUserSignedIn ? handleJoinFamily : redirectToSignIn}>
            {isUserSignedIn
              ? `Join the ${planLabel.charAt(0).toUpperCase() + planLabel.slice(1)}`
              : `Sign up to join the ${planLabel}`}
          </Button>
        </SignInPageLayout>
      )}
      <Modal
        open={showModal.open}
        title={modals.find((modal) => modal.id === showModal.id)?.title || ''}
        primaryButton={modals.find((modal) => modal.id === showModal.id)?.primaryButton}
        secondaryButtons={modals.find((modal) => modal.id === showModal.id)?.secondaryButtons}
        onClose={() => {
          setShowModal({ id: '', open: false });
          navigate(pages.home, { replace: true });
        }}
      >
        <p>{modals.find((modal) => modal.id === showModal.id)?.content}</p>
      </Modal>
    </div>
  );
}

export function Head() {
  return (
    <>
      <meta name="robots" content="noindex" />
      <title>{`Join the group - ${frontendHost}`}</title>
    </>
  );
}
